import { useEffectEvent, mergeRefs } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import type { EasingDefinition } from 'motion/react';
import { motion, useReducedMotion } from 'motion/react';
import type { ForwardedRef, ReactElement } from 'react';
import { forwardRef, useCallback, useRef, useState } from 'react';

import { noop } from '@infinex-private/ts-utils/function';
import { easing } from '@infinex/ui-constants';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';
import { isHtmlElement } from '@infinex/ui-utils/dom';

export type AnimateHeightProps = {
  /**
   * The content element to watch for height changes.
   */
  children: ReactElement;
  /**
   * The duration of the height animation, in seconds.
   * @default 0.3
   */
  duration?: number;
  /**
   * Optionally provide an easing function for the height animation.
   * @default [0.2, 0, 0, 1]
   */
  ease?: EasingDefinition;
  /**
   * Callback function that is called when the animation state changes.
   */
  onAnimationChange?: (isAnimating: boolean, height?: number) => void;
} & DOMProps &
  UnsafeStyleProps;

/**
 * A component that smoothly animates height changes when its children’s content
 * is updated.
 */
export const AnimateHeight = forwardRef(function AnimateHeight(
  props: AnimateHeightProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const prefersReducedMotion = useReducedMotion();
  const {
    className,
    duration = prefersReducedMotion ? 0 : 0.3,
    ease = easing.easeOutSmooth,
    onAnimationChange = noop,
    ...otherProps
  } = props;

  const [height, setHeight] = useState<number>();
  const measureHeight = useCallback((node: HTMLDivElement | null) => {
    const child = node?.firstElementChild;
    if (!isHtmlElement(child)) return;
    const ro = new ResizeObserver(() => setHeight(child.offsetHeight));
    ro.observe(child);
    return ro.disconnect;
  }, []);

  // resize observer fires on mount, so `initial=false` on motion.div is ineffective
  const isInitial = useRef(true);
  const getAnimationHandler = useEffectEvent(
    (type: 'start' | 'complete') => (def: { height: number }) => {
      if (isInitial.current) isInitial.current = type === 'start';
      else onAnimationChange(type === 'start', def.height);
    }
  );

  return (
    <motion.div
      {...otherProps}
      ref={mergeRefs(forwardedRef, measureHeight)}
      animate={{ height }}
      transition={{ duration, ease }}
      onAnimationStart={getAnimationHandler('start')}
      onAnimationComplete={getAnimationHandler('complete')}
      // "content" box-model accounts for any borders/padding added by consumers
      className={cn(className, 'box-content overflow-hidden')}
    />
  );
});
